import { client } from '../libs/client';
import { shop } from '../types/cms-types';
import { optimizeImage } from '../utils/imageOptimizer';

export interface ShopMenu {
  menu: string;
  price: number;
}

export interface ShopData {
  id: string;
  imageUrl: string;
  title: string;
  shopMenuList: ShopMenu[];
  description: string;
  labels: string[];
  category: string;
}

// MicroCMSの生データを正規化する関数
const transformShopData = (microCMSShop: shop<'get'>): ShopData => {
  // カテゴリーのマッピング
  const categoryMapping: { [key: string]: string } = {
    'ご飯 / 麺類': 'ご飯/麺類',
    揚げ物: '揚げ物',
    スイーツ: 'スイーツ',
    飲み物: '飲み物',
    その他: 'その他',
  };

  // 最初のカテゴリを使用（配列の場合）
  const firstCategory = microCMSShop.shop_category[0] || 'その他';
  const mappedCategory = categoryMapping[firstCategory] || 'その他';

  return {
    id: microCMSShop.id,
    imageUrl: optimizeImage(microCMSShop.shop_image.url, { width: 800, quality: 80 }),
    title: microCMSShop.shop_name,
    shopMenuList: microCMSShop.shop_menu_list.map((item) => ({
      menu: item.shop_menu,
      price: item.shop_menu_price,
    })),
    description: microCMSShop.shop_detail || '',
    labels: microCMSShop.shop_category,
    category: mappedCategory,
  };
};

export const fetchShops = async (): Promise<ShopData[]> => {
  try {
    const allShops: ShopData[] = [];
    let offset = 0;
    const limit = 100; // microCMSの1回のリクエストで取得する最大件数
    let hasMore = true;

    while (hasMore) {
      const response = await client.get({
        endpoint: 'shop',
        queries: {
          limit,
          offset,
          orders: '-createdAt',
        },
      });

      // MicroCMSの生データを正規化して追加
      const transformedShops = response.contents.map(transformShopData);
      allShops.push(...transformedShops);

      // 次のページがあるかチェック
      hasMore = response.contents.length === limit;
      offset += limit;
    }

    return allShops;
  } catch (error) {
    console.error('ショップデータの取得に失敗しました:', error);
    console.error('エラー詳細:', error);
    throw error;
  }
};

export const fetchShopsByCategory = async (category: string): Promise<ShopData[]> => {
  try {
    const allShops: ShopData[] = [];
    let offset = 0;
    const limit = 100; // microCMSの1回のリクエストで取得する最大件数
    let hasMore = true;

    while (hasMore) {
      const response = await client.get({
        endpoint: 'shop',
        queries: {
          limit,
          offset,
          orders: '-createdAt',
          filters: `shop_category[contains]${category}`,
        },
      });

      // MicroCMSの生データを正規化して追加
      const transformedShops = response.contents.map(transformShopData);
      allShops.push(...transformedShops);

      // 次のページがあるかチェック
      hasMore = response.contents.length === limit;
      offset += limit;
    }

    return allShops;
  } catch (error) {
    console.error(`カテゴリ「${category}」のショップデータの取得に失敗しました:`, error);
    throw error;
  }
};

export const categorizeShops = (shops: ShopData[]) => {
  const categorized = {
    'ご飯/麺類': [] as ShopData[],
    スイーツ: [] as ShopData[],
    揚げ物: [] as ShopData[],
    飲み物: [] as ShopData[],
    その他: [] as ShopData[],
  };

  // カテゴリーマッピング（MicroCMSのカテゴリ → フロントエンドのカテゴリ）
  const categoryMapping: { [key: string]: string } = {
    'ご飯 / 麺類': 'ご飯/麺類',
    揚げ物: '揚げ物',
    スイーツ: 'スイーツ',
    飲み物: '飲み物',
    その他: 'その他',
  };

  shops.forEach((shop) => {
    // labelsの各カテゴリをマッピングして追加
    const mappedCategories = shop.labels
      .map((label) => categoryMapping[label])
      .filter(
        (mappedCategory) =>
          mappedCategory && categorized[mappedCategory as keyof typeof categorized],
      );

    if (mappedCategories.length > 0) {
      mappedCategories.forEach((mappedCategory) => {
        categorized[mappedCategory as keyof typeof categorized].push(shop);
      });
    } else {
      // どのカテゴリにもマッピングされない場合は「その他」に追加
      categorized['その他'].push(shop);
    }
  });

  return categorized;
};
